import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { storage } from '../config/firebase';
import { ref, getDownloadURL } from 'firebase/storage';

const TutorialModal = ({ onClose, onComplete }) => {
  const [videoUrl, setVideoUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadVideoUrl();
  }, []);

  const loadVideoUrl = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Loading tutorial video from Firebase Storage...');
      // Get video URL from Firebase Storage
      // Note: filename matches the uploaded file "Travaili Tutorial.mp4"
      const videoRef = ref(storage, 'public/tutorial/Travaili Tutorial.mp4');
      const url = await getDownloadURL(videoRef);

      console.log('Tutorial video loaded successfully:', url);
      setVideoUrl(url);
      setLoading(false);
    } catch (err) {
      console.error('Failed to load tutorial video:', err);
      console.error('Error code:', err.code);
      console.error('Error message:', err.message);

      let errorMessage = 'Failed to load tutorial video.';

      if (err.code === 'storage/object-not-found') {
        errorMessage = 'Tutorial video not found. The video may not have been uploaded yet.';
      } else if (err.code === 'storage/unauthorized') {
        errorMessage = 'Access denied. Storage rules may not be deployed yet.';
      } else if (err.code === 'storage/unauthenticated') {
        errorMessage = 'Authentication required to view the tutorial.';
      }

      setError(errorMessage);
      setLoading(false);
    }
  };

  const handleSkip = () => {
    if (onComplete) {
      onComplete();
    }
    onClose();
  };

  const handleComplete = () => {
    if (onComplete) {
      onComplete();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-4 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold">Welcome to Travaili!</h2>
            <p className="text-sm text-blue-100 mt-1">Watch this quick tutorial to get started</p>
          </div>
          <button
            onClick={handleSkip}
            className="text-white hover:bg-blue-700 rounded-full p-2 transition"
            title="Close"
          >
            <i className="fas fa-times text-xl"></i>
          </button>
        </div>

        {/* Video Content */}
        <div className="flex-1 bg-black flex items-center justify-center overflow-hidden">
          {loading && (
            <div className="text-white text-center">
              <i className="fas fa-spinner fa-spin text-4xl mb-4"></i>
              <p>Loading tutorial video...</p>
            </div>
          )}

          {error && (
            <div className="text-white text-center p-6">
              <i className="fas fa-exclamation-triangle text-4xl mb-4 text-yellow-500"></i>
              <p className="mb-4">{error}</p>
              <button
                onClick={loadVideoUrl}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
              >
                <i className="fas fa-redo mr-2"></i>
                Retry
              </button>
            </div>
          )}

          {!loading && !error && videoUrl && (
            <div className="relative w-full h-full">
              <video
                key={videoUrl}
                controls
                controlsList="nodownload"
                className="w-full h-full max-h-[calc(90vh-180px)]"
                src={videoUrl}
                type="video/mp4"
                onEnded={handleComplete}
                preload="auto"
                playsInline
                onLoadStart={(e) => {
                  console.log('✅ Video load started');
                  console.log('Video URL:', videoUrl);
                }}
                onLoadedMetadata={(e) => {
                  console.log('✅ Video metadata loaded:', {
                    duration: e.target.duration,
                    videoWidth: e.target.videoWidth,
                    videoHeight: e.target.videoHeight,
                    readyState: e.target.readyState,
                    networkState: e.target.networkState
                  });
                  // Try to play, but handle if browser blocks it
                  e.target.play().catch(err => {
                    console.log('Autoplay blocked by browser:', err);
                    console.log('User needs to click play button');
                  });
                }}
                onLoadedData={(e) => {
                  console.log('✅ Video data loaded (can start playing)');
                }}
                onCanPlay={(e) => {
                  console.log('✅ Video can play - ready to play!');
                }}
                onProgress={(e) => {
                  if (e.target.buffered.length > 0) {
                    console.log('Video loading progress:', {
                      buffered: e.target.buffered.end(0),
                      duration: e.target.duration
                    });
                  }
                }}
                onStalled={(e) => {
                  console.warn('⚠️ Video loading stalled');
                }}
                onSuspend={(e) => {
                  console.log('Video loading suspended by browser');
                }}
                onError={(e) => {
                  console.error('❌ Video playback error:', e);
                  const error = e.target.error;
                  console.error('Error code:', error?.code);
                  console.error('Error message:', error?.message);
                  console.error('Error details:', {
                    MEDIA_ERR_ABORTED: error?.code === 1,
                    MEDIA_ERR_NETWORK: error?.code === 2,
                    MEDIA_ERR_DECODE: error?.code === 3,
                    MEDIA_ERR_SRC_NOT_SUPPORTED: error?.code === 4
                  });

                  let errorMsg = 'Video playback failed. ';
                  if (error?.code === 2) {
                    errorMsg += 'Network error while loading video.';
                  } else if (error?.code === 3) {
                    errorMsg += 'Video format is corrupted or not supported.';
                  } else if (error?.code === 4) {
                    errorMsg += 'Video format is not supported by your browser.';
                  }

                  setError(errorMsg);
                }}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-between items-center border-t border-gray-200">
          <div className="flex items-center text-sm text-gray-600">
            <i className="fas fa-info-circle mr-2 text-blue-500"></i>
            <span>You can replay this tutorial anytime from the user menu</span>
          </div>
          <button
            onClick={handleSkip}
            className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg font-medium hover:bg-gray-300 transition"
          >
            Skip Tutorial
          </button>
        </div>
      </div>
    </div>
  );
};

TutorialModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onComplete: PropTypes.func, // Optional callback when tutorial is completed
};

export default TutorialModal;
